import React, {useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import Select from 'react-select'
import AccountNotesFilterDropdown, {
  AccountNoteFilterValues,
  TagOption,
  CreatorOption,
  InterpreterOption,
} from './AccountNotesFilterDropdown'
import toaster from '../../../../Utils/toaster'
import {CommonLoading} from '../../../../Utils/commonLoading'
import VisibilityInAppointmentSelect, {
  VISIBILITY_LABELS,
} from '../../../components/VisibilityInAppointmentSelect'
import MultiSelectWithAll from '../../../components/MultiSelectWithAll'

const API_URL = process.env.REACT_APP_API_URL
const itemsPerPage = Number(process.env.REACT_APP_PAGINATION_ITEMS_PER_PAGE) || 10

type Props = {accountId?: number | string; readOnly?: boolean}

type NoteTag = {
  id: number
  name: string
}

type TaggedInterpreter = {
  id: string
  name: string
  email?: string
}

type NoteRow = {
  id: number
  accountId: number
  content: string
  isArchived: boolean
  createdDateTime: string
  createdBy?: string
  createdByName?: string
  modifiedDateTime?: string
  modifiedBy?: string
  modifiedByName?: string
  tags: NoteTag[]
  taggedInterpreters: TaggedInterpreter[]
  visibilityTypes: number[]
}

const AccountNotesView: React.FC<Props> = ({accountId, readOnly = false}) => {
  const [rows, setRows] = useState<NoteRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showTagsModal, setShowTagsModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active')
  const [filterData, setFilterData] = useState<AccountNoteFilterValues>({
    startDate: '',
    endDate: '',
    createdByIds: [],
    tagIds: [],
    interpreterIds: [],
  })
  const [formContent, setFormContent] = useState('')
  const [formTagIds, setFormTagIds] = useState<number[]>([])
  const [formInterpreterIds, setFormInterpreterIds] = useState<string[]>([])
  const [formVisibilityTypes, setFormVisibilityTypes] = useState<number[]>([])
  const [allTags, setAllTags] = useState<NoteTag[]>([])
  const [interpreterOptions, setInterpreterOptions] = useState<InterpreterOption[]>([])
  const [newTagName, setNewTagName] = useState('')
  const [creators, setCreators] = useState<CreatorOption[]>([])
  const latestRequestIdRef = useRef(0)

  const normalizedAccountId =
    typeof accountId === 'string' ? Number(accountId) : accountId

  const tagOptions: TagOption[] = useMemo(
    () => allTags.map((t) => ({value: t.id, label: t.name})),
    [allTags]
  )

  const payload = useMemo(() => {
    return {
      startDate: filterData.startDate ? new Date(filterData.startDate).toISOString() : undefined,
      endDate: filterData.endDate ? new Date(filterData.endDate).toISOString() : undefined,
      createdByIds: filterData.createdByIds?.length > 0 ? filterData.createdByIds : undefined,
      tagIds: filterData.tagIds?.length > 0 ? filterData.tagIds : undefined,
      interpreterIds: filterData.interpreterIds?.length > 0 ? filterData.interpreterIds : undefined,
      isArchived: viewMode === 'archived',
    }
  }, [filterData, viewMode])

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API_URL}/AccountNotes/tags`)
      if (res.data?.status === 'S') {
        setAllTags(res.data.result || [])
      }
    } catch (err) {
      console.error('fetchTags error', err)
    }
  }

  const fetchInterpreters = async (search = '') => {
    try {
      const res = await axios.get(`${API_URL}/accounts/by-type/interpreter`, {
        params: {items_per_page: 0, search},
      })
      const data = Array.isArray(res.data) ? res.data : []
      setInterpreterOptions(
        data
          .map((i: {
            id?: string
            Id?: string
            name?: string
            Name?: string
            email?: string
            Email?: string
          }) => {
            const id = String(i.id ?? i.Id ?? '').trim()
            if (!id) return null
            const name = String(i.name ?? i.Name ?? '').trim()
            const email = String(i.email ?? i.Email ?? '').trim()
            const label = name && email ? `${name} (${email})` : name || email || id
            return {value: id, label}
          })
          .filter((o): o is InterpreterOption => o !== null)
      )
    } catch (err) {
      console.error('fetchInterpreters error', err)
    }
  }

  const fetchCreators = async () => {
    if (!normalizedAccountId) return
    try {
      const res = await axios.get(`${API_URL}/AccountNotes/account/${normalizedAccountId}/creators`)
      if (res.data?.status === 'S') {
        const data = res.data.result || []
        setCreators(data.map((c: any) => ({value: c.id, label: c.name})))
      }
    } catch (err) {
      console.error('fetchCreators error', err)
    }
  }

  const fetchRows = async (page: number) => {
    if (!normalizedAccountId) return
    const requestId = ++latestRequestIdRef.current
    setIsLoading(true)
    try {
      const res = await axios.post(`${API_URL}/AccountNotes/account/${normalizedAccountId}`, payload, {
        params: {page, items_per_page: itemsPerPage, order: 'desc', sort: 'created'},
      })
      if (requestId !== latestRequestIdRef.current) return
      const {data, payload: meta} = res.data
      setRows(
        (data || []).map((row: any) => ({
          ...row,
          taggedInterpreters: row.taggedInterpreters || [],
          visibilityTypes: row.visibilityTypes || [],
        }))
      )
      setTotalPages(meta?.pagination?.last_page || 1)
      setCurrentPage(page)
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) return
      console.error('fetchRows error', err)
      toaster('error', 'Failed to load notes')
    } finally {
      if (requestId === latestRequestIdRef.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!readOnly) {
      fetchTags()
      fetchInterpreters()
    }
  }, [readOnly])

  useEffect(() => {
    if (normalizedAccountId && !readOnly) fetchCreators()
  }, [normalizedAccountId, readOnly])

  useEffect(() => {
    if (normalizedAccountId) fetchRows(1)
  }, [payload, normalizedAccountId])

  const resetForm = () => {
    setFormContent('')
    setFormTagIds([])
    setFormInterpreterIds([])
    setFormVisibilityTypes([])
  }

  const openCreate = () => {
    setEditingId(null)
    resetForm()
    setShowCreateModal(true)
  }

  const openEdit = (row: NoteRow) => {
    setEditingId(row.id)
    setFormContent(row.content)
    setFormTagIds(row.tags.map((t) => t.id))
    setFormInterpreterIds(row.taggedInterpreters.map((i) => i.id))
    setFormVisibilityTypes(row.visibilityTypes || [])
    setShowCreateModal(true)
  }

  const buildNotePayload = () => ({
    content: formContent,
    tagIds: formTagIds.length > 0 ? formTagIds : undefined,
    interpreterIds: formInterpreterIds.length > 0 ? formInterpreterIds : undefined,
    visibilityTypes:
      formInterpreterIds.length > 0 && formVisibilityTypes.length > 0
        ? formVisibilityTypes
        : undefined,
  })

  const createNote = async () => {
    if (!formContent.trim() || !normalizedAccountId) return
    setIsLoading(true)
    try {
      const res = await axios.post(`${API_URL}/AccountNotes`, {
        accountId: normalizedAccountId,
        ...buildNotePayload(),
      })
      if (res.data?.status === 'S') {
        toaster('success', 'Note created')
        resetForm()
        setShowCreateModal(false)
        fetchRows(currentPage)
        fetchCreators()
      } else {
        toaster('error', res.data?.text || 'Failed to create note')
      }
    } catch (err) {
      console.error('createNote error', err)
      toaster('error', 'Failed to create note')
    } finally {
      setIsLoading(false)
    }
  }

  const updateNote = async (id: number) => {
    if (!formContent.trim()) return
    setIsLoading(true)
    try {
      const res = await axios.put(`${API_URL}/AccountNotes/${id}`, buildNotePayload())
      if (res.data?.status === 'S') {
        toaster('success', 'Note updated')
        setShowCreateModal(false)
        setEditingId(null)
        fetchRows(currentPage)
      } else {
        toaster('error', res.data?.text || 'Failed to update note')
      }
    } catch (err) {
      console.error('updateNote error', err)
      toaster('error', 'Failed to update note')
    } finally {
      setIsLoading(false)
    }
  }

  const archiveNote = async (id: number) => {
    setIsLoading(true)
    try {
      const res = await axios.put(`${API_URL}/AccountNotes/${id}/archive`)
      if (res.data?.status === 'S') {
        toaster('success', 'Note archived')
        fetchRows(currentPage)
      } else {
        toaster('error', res.data?.text || 'Failed to archive note')
      }
    } catch (err) {
      console.error('archiveNote error', err)
      toaster('error', 'Failed to archive note')
    } finally {
      setIsLoading(false)
    }
  }

  const createTag = async () => {
    if (!newTagName.trim()) return
    setIsLoading(true)
    try {
      const res = await axios.post(`${API_URL}/AccountNotes/tags`, {name: newTagName})
      if (res.data?.status === 'S') {
        toaster('success', 'Tag created')
        setNewTagName('')
        fetchTags()
      } else {
        toaster('error', res.data?.text || 'Failed to create tag')
      }
    } catch (err) {
      console.error('createTag error', err)
      toaster('error', 'Failed to create tag')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDateTime = (isoString: string) => {
    const dt = new Date(isoString)
    return dt.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  return (
    <div
      className={`card ${readOnly ? 'shadow-none border-0' : 'shadow-sm'}`}
      style={readOnly ? {position: 'relative'} : undefined}
    >
      <div className='card-header d-flex align-items-center px-0'>
        <div className='card-title d-flex align-items-center gap-3'>
          <button
            type='button'
            className={`btn btn-sm ${viewMode === 'active' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setViewMode('active')}
          >
            Active Notes
          </button>
          <button
            type='button'
            className={`btn btn-sm ${viewMode === 'archived' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setViewMode('archived')}
          >
            Archived Notes
          </button>
        </div>
        {!readOnly && (
          <div className='card-toolbar d-flex align-items-end'>
            <div className='d-flex'>
              <div className='d-flex text-end'>
                <AccountNotesFilterDropdown
                  initialValues={filterData}
                  tagOptions={tagOptions}
                  creatorOptions={creators}
                  interpreterOptions={interpreterOptions}
                  onApply={(vals) => {
                    setFilterData(vals)
                    setCurrentPage(1)
                  }}
                  onReset={() => {
                    setFilterData({
                      startDate: '',
                      endDate: '',
                      createdByIds: [],
                      tagIds: [],
                      interpreterIds: [],
                    })
                    setCurrentPage(1)
                  }}
                />
              </div>
              {viewMode === 'active' && (
                <div className='d-flex align-items-end ms-2'>
                  <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-create'>Add Note</Tooltip>}>
                    <button type='button' className='btn btn-sm btn-primary btn-icon' onClick={openCreate}>
                      <i className='bi bi-plus fs-2'></i>
                    </button>
                  </OverlayTrigger>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={`card-body${readOnly ? ' px-0' : ''}`}>
        {!readOnly && (
          <>
        <Modal
          show={showCreateModal && !showTagsModal}
          onHide={() => {
            setShowCreateModal(false)
            setEditingId(null)
          }}
          centered
          backdrop='static'
          keyboard={!isLoading}
        >
          <Modal.Header className='py-3'>
            <Modal.Title className='fw-bold'>
              {editingId ? 'Edit Account Note' : 'Add Account Note'}
            </Modal.Title>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              onClick={() => {
                setShowCreateModal(false)
                setEditingId(null)
              }}
              aria-label='Close'
              disabled={isLoading}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
            </button>
          </Modal.Header>
          <Modal.Body>
            <div className='row g-3'>
              <div className='col-12'>
                <label className='form-label fs-7 mb-1'>Note Content</label>
                <textarea
                  className='form-control form-control-sm'
                  rows={4}
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder='Type your detailed note here...'
                />
              </div>
              <div className='col-12'>
                <div className='d-flex justify-content-between align-items-center mb-1'>
                  <label className='form-label fs-7 mb-0'>Tags</label>
                  <OverlayTrigger
                    placement='top'
                    overlay={<Tooltip id='tooltip-manage-tags'>Manage Tags</Tooltip>}
                  >
                    <button
                      type='button'
                      className='btn btn-icon btn-sm btn-light'
                      onClick={() => setShowTagsModal(true)}
                    >
                      <i className='bi bi-gear fs-6'></i>
                    </button>
                  </OverlayTrigger>
                </div>
                <Select
                  isMulti
                  classNamePrefix='react-select'
                  options={tagOptions}
                  value={tagOptions.filter((o) => formTagIds.includes(o.value))}
                  onChange={(opts: any) =>
                    setFormTagIds(opts ? opts.map((o: any) => o.value).slice(0, 3) : [])
                  }
                  placeholder='Select up to 3 tags'
                  isOptionDisabled={() => formTagIds.length >= 3}
                  menuPortalTarget={document.body}
                  styles={{menuPortal: (base: any) => ({...base, zIndex: 9999})}}
                />
                <small className='text-muted'>Max 3 tags allowed</small>
              </div>
              <MultiSelectWithAll
                label='Interpreter'
                options={interpreterOptions}
                value={formInterpreterIds}
                onChange={(ids) => {
                  setFormInterpreterIds(ids)
                  if (ids.length === 0) setFormVisibilityTypes([])
                }}
                onInputChange={(inputValue) => fetchInterpreters(inputValue)}
                placeholder='Select interpreter(s)'
              />
              <VisibilityInAppointmentSelect
                value={formVisibilityTypes}
                onChange={setFormVisibilityTypes}
                disabled={formInterpreterIds.length === 0}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className='py-3'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => {
                setShowCreateModal(false)
                setEditingId(null)
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={() => (editingId ? updateNote(editingId) : createNote())}
              disabled={isLoading || !formContent.trim()}
            >
              Save Note
            </button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showTagsModal}
          onHide={() => setShowTagsModal(false)}
          centered
          backdrop='static'
        >
          <Modal.Header className='py-3'>
            <Modal.Title className='fw-bold'>Manage Tags</Modal.Title>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              onClick={() => setShowTagsModal(false)}
              aria-label='Close'
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
            </button>
          </Modal.Header>
          <Modal.Body>
            <div className='mb-3'>
              <label className='form-label fs-7 mb-1'>Current Tags:</label>
              <div className='d-flex flex-wrap gap-2'>
                {allTags.map((tag) => (
                  <span key={tag.id} className='badge badge-light-primary text-dark fs-7'>
                    {tag.name}
                  </span>
                ))}
                {allTags.length === 0 && <span className='text-muted fs-7'>No tags yet</span>}
              </div>
            </div>
            <div className='d-flex gap-2 align-items-end'>
              <div className='flex-grow-1'>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='Type new tag name'
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  maxLength={100}
                />
              </div>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                onClick={createTag}
                disabled={!newTagName.trim() || allTags.length >= 15}
              >
                <i className='bi bi-plus me-1'></i>Add Tag
              </button>
            </div>
            {allTags.length >= 15 && (
              <small className='text-danger'>Maximum 15 tags allowed</small>
            )}
          </Modal.Body>
        </Modal>
          </>
        )}

        {!isLoading && rows.length === 0 ? (
          <div
            className='d-flex flex-column align-items-center justify-content-center'
            style={{minHeight: '45vh'}}
          >
            <div
              className='border rounded p-10 text-center bg-white'
              style={{maxWidth: 560, width: '100%'}}
            >
              <div className='mb-6'>
                <i className='bi bi-journal-text text-gray-500' style={{fontSize: '3.5rem'}}></i>
              </div>
              <div className='fs-3 fw-bold mb-2'>
                {viewMode === 'active' ? 'No notes added yet.' : 'No archived notes.'}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='row g-4'>
              {rows.map((row) => (
                <div key={row.id} className='col-12 col-md-6'>
                  <div className='card border h-100'>
                    <div className='card-body p-4'>
                      <div className='d-flex justify-content-between align-items-start mb-2'>
                        <div>
                          <span className='fw-semibold text-dark'>{row.createdByName || 'User'}</span>
                          <span className='text-muted ms-2 fs-7'>
                            {formatDateTime(row.createdDateTime)}
                          </span>
                        </div>
                      </div>
                      <p className='text-gray-700 mb-3' style={{whiteSpace: 'pre-wrap'}}>
                        {row.content}
                      </p>
                      <div className='d-flex flex-wrap gap-2 mb-3'>
                        {row.tags.map((tag) => (
                          <span key={tag.id} className='badge badge-light-secondary text-dark fs-8'>
                            {tag.name}
                          </span>
                        ))}
                        {row.taggedInterpreters.map((interpreter) => (
                          <span key={interpreter.id} className='badge badge-light-info text-dark fs-8'>
                            {interpreter.name}
                          </span>
                        ))}
                        {row.visibilityTypes.map((visibility) => (
                          <span key={visibility} className='badge badge-light-warning text-dark fs-8'>
                            {VISIBILITY_LABELS[visibility] || visibility}
                          </span>
                        ))}
                      </div>
                      {!readOnly && !row.isArchived && (
                        <div className='d-flex justify-content-end gap-2'>
                          <OverlayTrigger
                            placement='top'
                            overlay={<Tooltip id={`edit-${row.id}`}>Edit</Tooltip>}
                          >
                            <button
                              type='button'
                              className='btn btn-icon btn-sm btn-light-primary'
                              onClick={() => openEdit(row)}
                            >
                              <i className='bi bi-pencil'></i>
                            </button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement='top'
                            overlay={<Tooltip id={`archive-${row.id}`}>Archive</Tooltip>}
                          >
                            <button
                              type='button'
                              className='btn btn-icon btn-sm btn-light-danger'
                              onClick={() => archiveNote(row.id)}
                            >
                              <i className='bi bi-archive'></i>
                            </button>
                          </OverlayTrigger>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className='d-flex justify-content-between align-items-center mt-4'>
              <div>
                Page {currentPage} of {totalPages}
              </div>
              <div>
                <button
                  className='btn btn-sm btn-light me-2'
                  disabled={currentPage <= 1}
                  onClick={() => fetchRows(currentPage - 1)}
                >
                  Prev
                </button>
                <button
                  className='btn btn-sm btn-light'
                  disabled={currentPage >= totalPages}
                  onClick={() => fetchRows(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
        {isLoading && <CommonLoading contained={readOnly} />}
      </div>
    </div>
  )
}

export default AccountNotesView
